/**
 * Pure TEA update for the narrow-layout bottom sheet (ZW-276).
 *
 * Shell: `OutlineSurface.vue` (side="bottom"). The sheet opens at half
 * height; dragging the handle up expands it to full height, dragging it down
 * shrinks it or closes it. A tap on the handle toggles the height.
 */
import { assertNever } from "../../lib/assertNever";
import { isNothing, just, nothing, type Maybe } from "../../lib/maybe";
import type { UpdateFn } from "../../lib/tea";

/** Drag distance (px) that counts as a swipe rather than a tap. */
export const SHEET_SWIPE_PX = 48;

export type BottomSheetModel = {
  readonly expanded: boolean;
  /** Pointer Y where the current drag started. */
  readonly dragStartY: Maybe<number>;
  /** Live drag distance; negative is up. */
  readonly dragOffset: number;
};

export const initialBottomSheetModel: BottomSheetModel = {
  expanded: false,
  dragStartY: nothing,
  dragOffset: 0,
};

export type BottomSheetMsg =
  | { readonly type: "Reset" }
  | { readonly type: "DragStarted"; readonly y: number }
  | { readonly type: "DragMoved"; readonly y: number }
  | { readonly type: "DragEnded" }
  | { readonly type: "ToggleExpanded" };

export type BottomSheetCmd = { readonly kind: "close" };

export function updateBottomSheet(
  model: BottomSheetModel,
  msg: BottomSheetMsg,
): readonly [BottomSheetModel, readonly BottomSheetCmd[]] {
  switch (msg.type) {
    case "Reset":
      return [initialBottomSheetModel, []];
    case "DragStarted":
      return [{ ...model, dragStartY: just(msg.y), dragOffset: 0 }, []];
    case "DragMoved":
      if (isNothing(model.dragStartY)) return [model, []];
      return [{ ...model, dragOffset: msg.y - model.dragStartY.value }, []];
    case "DragEnded": {
      if (isNothing(model.dragStartY)) return [model, []];
      const settled = { ...model, dragStartY: nothing, dragOffset: 0 };
      if (model.dragOffset <= -SHEET_SWIPE_PX) {
        return [{ ...settled, expanded: true }, []];
      }
      if (model.dragOffset >= SHEET_SWIPE_PX) {
        return model.expanded
          ? [{ ...settled, expanded: false }, []]
          : [settled, [{ kind: "close" }]];
      }
      // A tap (or a short drag) toggles between half and full height.
      return [{ ...settled, expanded: model.expanded === false }, []];
    }
    case "ToggleExpanded":
      return [{ ...model, expanded: model.expanded === false }, []];
    default:
      return assertNever(msg);
  }
}

export const updateBottomSheetFn: UpdateFn<
  BottomSheetModel,
  BottomSheetMsg,
  BottomSheetCmd
> = updateBottomSheet;
